import { useCallback, useState } from 'react';

import { COLUMN_DEFS, isColumnId, visibleDefs, type ColumnId } from './columns';

// Columnas elegidas en la Lista Maestra, recordadas en este navegador por empresa y por
// usuario. Se guardan las opcionales a la vista y los campos propios OCULTOS: un campo
// que Calidad define se ve por defecto, como promete Catálogos. Un valor dañado vuelve
// al mockup.

export function columnsStorageKey(companyId: string | null, userKey: string | null): string {
  return `qms.master-list.columns.v2:${companyId ?? '-'}:${userKey ?? '-'}`;
}

interface Stored {
  optional: ColumnId[];
  hiddenCustom: string[];
}

const EMPTY: Stored = { optional: [], hiddenCustom: [] };

function read(key: string): Stored {
  try {
    const raw: unknown = JSON.parse(localStorage.getItem(key) ?? 'null');
    if (typeof raw !== 'object' || raw === null) {
      return EMPTY;
    }
    const { optional, hiddenCustom } = raw as Record<string, unknown>;
    if (!Array.isArray(optional) || !Array.isArray(hiddenCustom)) {
      return EMPTY;
    }
    const opt = optional.filter((x): x is ColumnId => typeof x === 'string' && isColumnId(x));
    const cf = hiddenCustom.filter((x): x is string => typeof x === 'string');
    if (opt.length !== optional.length || cf.length !== hiddenCustom.length) {
      return EMPTY;
    }
    return { optional: opt, hiddenCustom: cf };
  } catch {
    return EMPTY;
  }
}

function write(key: string, s: Stored): void {
  try {
    localStorage.setItem(key, JSON.stringify(s));
  } catch {
    // Sin almacenamiento (modo privado estricto): la elección dura lo que la pestaña.
  }
}

const toggled = <T>(list: readonly T[], item: T): T[] =>
  list.includes(item) ? list.filter((x) => x !== item) : [...list, item];

export interface VisibleColumns {
  /** Ids a mostrar, en el orden de la tabla. */
  columns: ColumnId[];
  /** Claves de los campos propios ocultos (los demás se ven). */
  hiddenCustom: string[];
  toggle: (id: ColumnId) => void;
  toggleCustom: (key: string) => void;
  reset: () => void;
}

export function useVisibleColumns(
  companyId: string | null,
  userKey: string | null,
): VisibleColumns {
  const key = columnsStorageKey(companyId, userKey);
  // Estado por clave: al cambiar de empresa o de usuario se lee la elección de esa clave.
  const [state, setState] = useState<{ key: string; stored: Stored }>(() => ({
    key,
    stored: read(key),
  }));
  const stored = state.key === key ? state.stored : read(key);

  const update = useCallback(
    (next: (s: Stored) => Stored) => {
      setState((prev) => {
        const value = next(prev.key === key ? prev.stored : read(key));
        write(key, value);
        return { key, stored: value };
      });
    },
    [key],
  );

  const toggle = useCallback(
    (id: ColumnId) => {
      if (COLUMN_DEFS.find((c) => c.id === id)?.optional !== true) {
        return;
      }
      update((s) => ({ ...s, optional: toggled(s.optional, id) }));
    },
    [update],
  );
  const toggleCustom = useCallback(
    (cf: string) => update((s) => ({ ...s, hiddenCustom: toggled(s.hiddenCustom, cf) })),
    [update],
  );
  const reset = useCallback(() => update(() => EMPTY), [update]);

  return {
    columns: visibleDefs(stored.optional).map((c) => c.id),
    hiddenCustom: stored.hiddenCustom,
    toggle,
    toggleCustom,
    reset,
  };
}
